import { Box, Checkbox, FormControlLabel, Radio, Stack, SxProps, Typography } from '@mui/material';
import React, { PropsWithChildren, useMemo } from 'react';
import Icon from '@mdi/react';
import { labelFontSemiBold, bodyFont, vars } from '../../../../../Theme';
import { WorkflowOption } from '../../../../../redux/project/sliceProjectWorkflow';
import { useSelector } from 'react-redux';
import { getSignatureStatusByStatusNumber } from '../../../../../redux/project/sliceProjectSignatureStatus';
import { useTranslation } from 'react-i18next';

/** Styles for the Main Box component. */
const boxSx: SxProps = {
    border: `1px solid ${vars.lightGray}`,
    borderRadius: '4px',
    px: 1.5,
    py: 0.5,
    width: '100%',
    mb: 1,
};

/** Styles for the ForwardOption component. */
const forwardOptionSx: SxProps = {
    alignItems: 'flex-start',
    '& .MuiButtonBase-root ': {
        px: 1,
        py: 0,
    },
};

const LabelComponent = ({ option }: { option: WorkflowOption }) => {
    /** Access relevant state variables from the redux store. */
    const signatureStatus = useSelector(getSignatureStatusByStatusNumber);
    const { i18n } = useTranslation();
    const computedProps = useMemo(() => {
        const signatureStatusData = signatureStatus(option.connectorSignStatus);
        return {
            label: option.connectorDescription?.split('->').pop(),
            signatureStatusIcon: signatureStatusData?.icon ?? '',
            iconColor: signatureStatusData?.color ?? vars.font,
            signatureStatusLabel: signatureStatusData?.translations?.[i18n.language] ?? '',
        };
    }, [option]);

    return (
        <Stack direction='row' sx={{ ml: 1.5 }}>
            <Box>
                <Icon
                    path={computedProps.signatureStatusIcon}
                    color={computedProps.iconColor}
                    size={1}
                />
            </Box>

            <Stack sx={{ ml: 1.5 }}>
                <Typography sx={{ ...labelFontSemiBold, color: vars.font }}>
                    {computedProps.signatureStatusLabel}
                </Typography>
                <Typography sx={{ ...bodyFont, fontSize: '14px', color: vars.font }}>
                    {computedProps.label}
                </Typography>
            </Stack>
        </Stack>
    );
};

export default (props: IForwardOptionProps) => {
    const { option, type, checked, handleChange, disabled, hasError } = props;

    return (
        <Box
            sx={{
                ...boxSx,
                border: `1px solid ${hasError ? vars.errorDialogRed : vars.lightGray}`,
            }}
        >
            <FormControlLabel
                sx={forwardOptionSx}
                value={option.path}
                disabled={disabled}
                control={
                    type === 'radio' ? (
                        <Radio
                            checked={checked ?? false}
                            value={option.path}
                            onChange={handleChange}
                            name={option.path}
                        />
                    ) : (
                        <Checkbox
                            checked={checked ?? false}
                            value={option.path}
                            onChange={handleChange}
                            name={option.path}
                        />
                    )
                }
                label={<LabelComponent option={option} />}
            />
            {props.children}
        </Box>
    );
};

/**
 * Interface that describes the props for an ForwardOption component.
 */
interface IForwardOptionProps extends PropsWithChildren {
    /** Task description. */
    option: WorkflowOption;

    /** Type of option. */
    type: 'radio' | 'check';

    /** Defines if Checkbox component is checked. */
    checked?: boolean;

    disabled?: boolean;

    hasError?: boolean;

    /** Changes Checkbox component. */
    handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}
